/**
 * RecipientList - "here's exactly who gets emailed" block.
 *
 * Atomic Design role: Molecule. Sending mail on someone's behalf is always a
 * deliberate choice in this app, and the same question has to be answered
 * before every send: WHO, at WHICH address. Three prompts ask it —
 *
 *	 - the technician's pre-submit prompt at the end of a checklist,
 *	 - the reviewer's "Email Contact to Verify" action on the detail page,
 *	 - the admin's "Notify users" prompt before any work order / inspection save,
 *
 * — and all three render this, rather than three hand-written lists free to
 * drift apart.
 *
 * Read-only by default. Pass `selectable` and the admin prompt's mode turns
 * on: a checkbox per row, so individual people can be dropped from a send
 * without cancelling it. Callers gate their buttons with
 * `reachableRecipients()` so the copy and the behaviour can't disagree about
 * who counts.
 *
 * Was VerificationRecipientList; renamed when the admin prompt made it general.
 *
 * @example Read-only — name the people a verification request would reach
 * <RecipientList recipients={reachableRecipients(contacts)} lead="Submitting will also email" purpose="to verify and sign" />
 *
 * @example Selectable — let an admin drop individual recipients
 * <RecipientList recipients={recipients} selectable selectedEmails={kept} onToggleRecipient={toggle} />
 */
import React from 'react';
import { Checkbox } from '../../atoms';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * One person an email would go to.
 *
 * Not necessarily a platform user — the on-site contact and the inspection's
 * "Send to Client" recipient are bare addresses on the record, which is why
 * this is an address plus a display name rather than a User.
 */
export interface Recipient {
	/** Display name, when one is on file */
	name?: string | null;
	/** Address the email would be sent to */
	email: string;
	/**
	 * Why this person is on the list — "Assignee", "Notification watch list",
	 * "Customer contact". Rendered as a third, muted line. It's what makes a
	 * selectable list actionable: it tells the admin which rows are the
	 * obvious keeps and which are the ones worth sparing on a minor edit.
	 */
	reason?: string | null;
}

export interface RecipientListProps {
	/** Everyone the email would reach. Filtered defensively. */
	recipients: Recipient[];
	/**
	 * Sentence prefix, so the block reads naturally wherever it sits:
	 * "Submitting will also email this person:" vs "This will email these
	 * people:".
	 */
	lead?: string;
	/**
	 * Optional tail on the lead sentence naming what they're being asked for
	 * — e.g. "to verify and sign". Omitted for a plain notification.
	 */
	purpose?: string;
	/** Copy shown when there's nobody to email. */
	emptyText?: string;
	/**
	 * Render a checkbox per row. The parent owns the selection — this
	 * molecule only reports the toggles.
	 */
	selectable?: boolean;
	/** Addresses currently checked. Compared case-insensitively. */
	selectedEmails?: string[];
	/** Called with the row's address and its new checked state. */
	onToggleRecipient?: (email: string, selected: boolean) => void;
	/** Optional outer className. */
	className?: string;
}

/* ============================================
 * HELPERS
 * ============================================ */

/**
 * The recipients we can actually reach. Someone with no address on file would
 * otherwise render as a name with nothing under it, and the server has
 * nowhere to send anyway.
 *
 * Exported so callers gate their buttons and labels on the same rule this
 * component renders by.
 */
export function reachableRecipients(recipients?: Recipient[] | null): Recipient[] {
	return (recipients ?? []).filter((r) => !!r.email && r.email.trim() !== '');
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const RecipientList: React.FC<RecipientListProps> = ({
	recipients,
	lead = 'This will email',
	purpose,
	emptyText = 'Nobody will be emailed — there is no address on file.',
	selectable = false,
	selectedEmails,
	onToggleRecipient,
	className,
}) => {
	const reachable = reachableRecipients(recipients);

	/* Case-insensitive membership, matching how the server matches the
	   allow-list it's sent back. */
	const selected = new Set((selectedEmails ?? []).map((email) => email.toLowerCase()));
	const isSelected = (email: string) => selected.has(email.toLowerCase());

	if (reachable.length === 0) {
		return (
			<p className={`recipient-list text-14px font-sans text-brand-text${className ? ` ${className}` : ''}`} data-ref="molecule-recipient-list">
				{emptyText}
			</p>
		);
	}

	/* "…will email these people to verify and sign:" — the purpose tail is
	   optional so a plain notification doesn't have to invent one. */
	const leadSentence = [lead, reachable.length === 1 ? 'this person' : 'these people', purpose].filter(Boolean).join(' ');

	return (
		<div className={`recipient-list${className ? ` ${className}` : ''}`} data-ref="molecule-recipient-list">
			<p className="recipient-list__lead text-14px font-sans text-brand-text mb-8px">{`${leadSentence}:`}</p>
			{/* Name AND address spelled out — the whole point is that nobody is
			    surprised by who got mailed. */}
			<ul className="recipient-list__items rounded-md border border-brand-border divide-y divide-brand-border">
				{reachable.map((recipient) => {
					/* The row's text is identical either way; only the wrapper
					   differs, so a read-only list can't pick up a label's
					   click target or a checkbox's spacing. */
					const details = (
						<span className="recipient-list__details block min-w-0">
							<span className="block text-14px font-sans font-semibold text-brand-text">{recipient.name || 'Contact'}</span>
							<span className="block text-13px font-sans text-brand-text-muted break-all">{recipient.email}</span>
							{recipient.reason && <span className="block mt-2px text-12px font-sans text-brand-text-subtle">{recipient.reason}</span>}
						</span>
					);

					return (
						<li key={recipient.email} className="recipient-list__item">
							{selectable ? (
								<Checkbox checked={isSelected(recipient.email)} onChange={(checked) => onToggleRecipient?.(recipient.email, checked)} label={details} className="recipient-list__toggle w-full px-12px py-10px hover:bg-brand-surface transition-colors" dataRef="molecule-recipient-list-item" />
							) : (
								<div className="px-12px py-8px">{details}</div>
							)}
						</li>
					);
				})}
			</ul>
		</div>
	);
};

export default RecipientList;
